import { Box, Typography, Chip } from '@mui/material'
import {
  RadioButtonChecked as PickupIcon,
  RadioButtonUnchecked as DropIcon,
  SwapVert as TransitIcon,
  LocalShipping as EmptyMoveIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Inventory as PackageIcon
} from '@mui/icons-material'
import { LoadStop, LoadPackage } from '@/types/load'
import { formatStopAddress, getFacilityName } from '@/utils/addressFormat'
import { formatScheduleWindow, formatCheckTime } from '@/utils/dateConversion'
import {
  getStopTypeLabel,
  getStopTypeColor,
  getNormalizedStopType,
  getStopStatus,
  getStopNumber,
  getStopTimezone,
  getStopCheckTimezone,
  hasCheckTimes,
  isEmptyMoveStop,
  needsActiveAppointment,
  NO_ACTIVE_APPOINTMENT
} from '@/utils/stopUtils'

interface StopsListProps {
  stops: LoadStop[]
  packages?: LoadPackage[]
}

/**
 * Map schedule type to display label
 * Matches main B18 app schedule type options
 */
const getScheduleTypeLabel = (scheduleType?: string): string => {
  if (!scheduleType) return ''
  const lower = scheduleType.toLowerCase()
  if (lower === 'fcfs' || lower === 'open_fcfs' || lower.includes('open')) {
    return 'Open/FCFS'
  }
  if (
    lower === 'byappointment' ||
    lower.includes('appt') ||
    lower.includes('appointment')
  ) {
    return 'By Appointment'
  }
  // Return original if unknown type
  return scheduleType
}

const getStopIcon = (stopType: string) => {
  if (stopType === 'empty-move') {
    return <EmptyMoveIcon fontSize="small" sx={{ color: 'warning.main' }} />
  }
  if (stopType.includes('pickup')) {
    return <PickupIcon fontSize="small" sx={{ color: 'primary.main' }} />
  }
  if (stopType.includes('drop')) {
    return <DropIcon fontSize="small" sx={{ color: 'error.main' }} />
  }
  return <TransitIcon fontSize="small" sx={{ color: 'grey.500' }} />
}

const hasScheduleSide = (side?: LoadStop['scheduleStart']): boolean =>
  typeof side?.seconds === 'number'

/**
 * Text for a stop's schedule row, or '' to hide the row.
 * Regular stops always read like the web's Dispatch tab stop row
 * (StopRow.tsx), so a missing side says 'Not Scheduled'.
 */
const getScheduleRowText = (
  stop: LoadStop,
  timezone: string | undefined
): string => {
  if (needsActiveAppointment(stop)) return NO_ACTIVE_APPOINTMENT
  if (isEmptyMoveStop(stop)) {
    // Empty moves keep the plugin's old rule until product decides whether
    // they should read 'Auto-calculated' like the web's Dispatch tab.
    if (stop.scheduleStart?.seconds) {
      return formatScheduleWindow(
        stop.scheduleStart,
        stop.scheduleEnd,
        timezone
      )
    }
    return stop.appointmentStart || ''
  }
  // appointmentStart is old plugin data the web never had; it only fills a
  // stop with no schedule at all, so real schedule data always wins.
  if (
    !hasScheduleSide(stop.scheduleStart) &&
    !hasScheduleSide(stop.scheduleEnd) &&
    stop.appointmentStart
  ) {
    return stop.appointmentStart
  }
  return formatScheduleWindow(stop.scheduleStart, stop.scheduleEnd, timezone)
}

/**
 * True when a package quantity or weight has a value worth showing.
 * Like the web app, 0 counts as missing.
 */
const hasAmount = (value?: number | string): boolean =>
  value != null && String(value).trim() !== '' && Number(value) !== 0

/**
 * Get packages associated with a stop
 */
const getStopPackages = (
  stop: LoadStop,
  allPackages?: LoadPackage[]
): LoadPackage[] => {
  if (!allPackages?.length || !stop.packages?.length) return []

  // Get package UIDs from stop
  const stopPackageUids = stop.packages.map(p => p.uid).filter(Boolean)

  // Find matching packages
  return allPackages.filter(pkg => pkg.uid && stopPackageUids.includes(pkg.uid))
}

const StopsList = ({ stops, packages }: StopsListProps) => {
  if (!stops || stops.length === 0) {
    return (
      <Box sx={{ p: 2.5 }}>
        <Typography variant="body2" color="text.secondary">
          No stops scheduled
        </Typography>
      </Box>
    )
  }

  // P2-1315 — stops arrive already ordered by the parent (getOrderedRouteStops):
  // dispatch order, with each empty move bundled into its own segment. Render
  // as-is. Do NOT re-sort by index here — EM stops carry placeholder indices,
  // so an index sort mislays a 2nd driver's EM-2 to the front of the route.
  const sortedStops = stops

  return (
    <Box sx={{ p: 2.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
        Stops ({stops.length})
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {sortedStops.map((stop, index) => {
          const stopType = getNormalizedStopType(stop)
          const stopStatus = getStopStatus(stop)
          const timezone = getStopTimezone(stop)
          const checkTimezone = getStopCheckTimezone(stop)
          const stopNumber = getStopNumber(stop, index)
          const stopPackages = getStopPackages(stop, packages)
          const scheduleText = getScheduleRowText(stop, timezone)

          return (
            <Box
              key={stop._id || stop.uid || index}
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
            >
              {/* Icon with connecting line */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}
              >
                {getStopIcon(stopType)}
                {index < sortedStops.length - 1 && (
                  <Box
                    sx={{
                      width: 1,
                      minHeight: 48,
                      height: 'calc(100% - 20px)',
                      bgcolor: 'grey.300',
                      mt: 0.5
                    }}
                  />
                )}
              </Box>

              {/* Stop details */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Header row: Type badge + Status */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 0.5,
                    flexWrap: 'wrap'
                  }}
                >
                  <Chip
                    label={`${getStopTypeLabel(stopType)} #${stopNumber}`}
                    size="small"
                    variant="outlined"
                    color={getStopTypeColor(stopType)}
                    sx={{ height: 20, fontSize: '0.625rem', fontWeight: 500 }}
                  />
                  {stopStatus.label !== 'Pending' && (
                    <Chip
                      icon={<CheckIcon sx={{ fontSize: 12 }} />}
                      label={stopStatus.label}
                      size="small"
                      color={
                        stopStatus.color as 'success' | 'warning' | 'default'
                      }
                      sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        '& .MuiChip-icon': { ml: 0.5 }
                      }}
                    />
                  )}
                </Box>

                {/* Facility name + address.
                    Transit stops and empty moves usually have no facility —
                    promote the city/state address to the primary line so
                    we don't render placeholder "Unknown Facility" text.
                    Mirrors the web dispatch tab behavior. */}
                {(() => {
                  const facility = getFacilityName(stop)
                  const address = formatStopAddress(stop)
                  if (facility) {
                    return (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {facility}
                        </Typography>
                        {address && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', lineHeight: 1.4 }}
                          >
                            {address}
                          </Typography>
                        )}
                      </>
                    )
                  }
                  if (address) {
                    return (
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {address}
                      </Typography>
                    )
                  }
                  return null
                })()}

                {/* Schedule window */}
                {scheduleText && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mt: 0.5,
                      flexWrap: 'wrap'
                    }}
                  >
                    <ScheduleIcon
                      sx={{ fontSize: 12, color: 'text.secondary' }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {scheduleText}
                    </Typography>
                    {stop.scheduleType && (
                      <Chip
                        label={getScheduleTypeLabel(stop.scheduleType)}
                        size="small"
                        variant="outlined"
                        sx={{ height: 14, fontSize: '0.55rem', ml: 0.5 }}
                      />
                    )}
                  </Box>
                )}

                {/* Check-in/out times */}
                {hasCheckTimes(stop) && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      mt: 0.5
                    }}
                  >
                    {!!(stop.checkIn || stop.checkedIn) && (
                      <Typography
                        variant="caption"
                        sx={{ color: 'success.main' }}
                      >
                        In:{' '}
                        {formatCheckTime(
                          stop.checkIn || stop.checkedIn,
                          checkTimezone
                        )}
                      </Typography>
                    )}
                    {!!(stop.checkOut || stop.checkedOut) && (
                      <Typography
                        variant="caption"
                        sx={{ color: 'success.main' }}
                      >
                        Out:{' '}
                        {formatCheckTime(
                          stop.checkOut || stop.checkedOut,
                          checkTimezone
                        )}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Package details */}
                {stopPackages.length > 0 && (
                  <Box sx={{ mt: 0.5, pl: 0.5 }}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <PackageIcon
                        sx={{ fontSize: 12, color: 'text.secondary' }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        Packages ({stopPackages.length})
                      </Typography>
                    </Box>
                    {stopPackages.map((pkg, pkgIdx) => {
                      const eventType = stopType.includes('drop')
                        ? 'drop'
                        : 'pickup'
                      const note =
                        eventType === 'drop' ? pkg.dropNote : pkg.pickupNote
                      return (
                        <Box
                          key={pkg._id || pkg.uid || pkgIdx}
                          sx={{ pl: 2, mt: 0.25 }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {eventType === 'drop' ? 'DP#' : 'PU#'}:{' '}
                            {pkg.packageId?.replace(/[^a-zA-Z0-9\s]/g, '') ||
                              `PKG-${pkgIdx + 1}`}
                            {pkg.unitType && ` • ${pkg.unitType}`}
                            {hasAmount(pkg.count) && ` • Qty: ${pkg.count}`}
                            {hasAmount(pkg.weight) && ` • ${pkg.weight} lbs`}
                          </Typography>
                          {note && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                pl: 1,
                                color: 'text.disabled',
                                fontStyle: 'italic'
                              }}
                            >
                              {note}
                            </Typography>
                          )}
                        </Box>
                      )
                    })}
                  </Box>
                )}

                {/* Facility notes */}
                {stop.facilityNotes && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 0.5,
                      color: 'text.disabled',
                      fontStyle: 'italic'
                    }}
                  >
                    Note: {stop.facilityNotes}
                  </Typography>
                )}
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default StopsList
